import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API } from "../config/api";
import { Newspaper, ExternalLink, Search, RefreshCw, Calendar } from "lucide-react";

/**
 * Company News tab — aggregates past-30-day news for a ticker,
 * sorted newest first, with a search filter.
 */
export default function CompanyNews({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const fetchNews = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/stock/news-aggregator/${ticker}`, { timeout: 40000 });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load news");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchNews();
    return () => { cancelled = true; };
  }, [ticker, refreshKey]);

  // Normalize + filter to last 30 days + sort newest first + apply search
  const articles = useMemo(() => {
    const raw = data?.news || [];
    const cutoff = Date.now() / 1000 - 30 * 24 * 3600;
    const normalized = raw.map((a) => {
      let ts = 0;
      if (typeof a.timestamp === "number" && a.timestamp > 0) ts = a.timestamp;
      else if (a.published) {
        const parsed = Date.parse(a.published);
        if (!isNaN(parsed)) ts = parsed / 1000;
      }
      return { ...a, _ts: ts };
    });
    const filtered = normalized.filter((a) => !a._ts || a._ts >= cutoff);
    filtered.sort((a, b) => (b._ts || 0) - (a._ts || 0));
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      return filtered.filter((a) => (a.title || "").toLowerCase().includes(qq));
    }
    return filtered;
  }, [data, q]);

  const formatRelative = (ts) => {
    if (!ts) return "";
    const now = Date.now() / 1000;
    const diff = now - ts;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    const days = Math.floor(diff / 86400);
    if (days < 30) return `${days}d ago`;
    return new Date(ts * 1000).toLocaleDateString();
  };

  return (
    <div className="space-y-3" data-testid="company-news-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-blue-400" />
          <h2 className="text-lg font-semibold text-white">{ticker} Company News</h2>
          <span className="text-[10px] text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Past 30 days
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter articles…"
              data-testid="company-news-search"
              className="pl-7 pr-3 py-1.5 text-xs bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-blue-500 w-48"
            />
          </div>
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={loading}
            data-testid="company-news-refresh"
            className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700 disabled:opacity-50"
            title="Refresh news"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="py-8 text-center text-gray-500 text-sm">Loading news…</div>
      )}

      {error && (
        <div className="p-3 rounded border border-red-900/40 bg-red-950/20 text-red-300 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && articles.length === 0 && (
        <div className="py-10 text-center text-gray-500 text-sm">
          No articles found for {ticker} in the past 30 days
          {q ? ` matching "${q}"` : ""}.
        </div>
      )}

      <div className="space-y-2">
        {articles.map((a, idx) => {
          const sentColor = a.sentiment === "positive"
            ? "border-green-800/50 bg-green-950/10"
            : a.sentiment === "negative"
            ? "border-red-800/50 bg-red-950/10"
            : "border-gray-800 bg-gray-900/40";
          const sentBadge = a.sentiment === "positive"
            ? "bg-green-900/60 text-green-300"
            : a.sentiment === "negative"
            ? "bg-red-900/60 text-red-300"
            : "bg-gray-800 text-gray-400";

          return (
            <a
              key={`${a.link || idx}-${idx}`}
              href={a.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`company-news-article-${idx}`}
              className={`block rounded-lg border ${sentColor} p-3 hover:border-blue-700 hover:bg-gray-800/40 transition-colors group`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white leading-snug group-hover:text-blue-300">
                    {a.title}
                    <ExternalLink className="inline-block w-3 h-3 ml-1 opacity-50 align-baseline" />
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500 flex-wrap">
                    <span className="text-gray-400">{a.publisher || a.source}</span>
                    {a._ts > 0 && <span>• {formatRelative(a._ts)}</span>}
                    {a.sentiment && a.sentiment !== "neutral" && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase ${sentBadge}`}>
                        {a.sentiment}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </a>
          );
        })}
      </div>

      {data && articles.length > 0 && (
        <div className="pt-2 text-[10px] text-gray-500 text-center">
          Showing {articles.length} of {data.total_count || 0} articles • sources: {(data.sources || []).join(", ")}
        </div>
      )}
    </div>
  );
}
